import {
  PromptInput,
  PromptInputAttachment,
  PromptInputAttachments,
  PromptInputBody,
  PromptInputSpeechButton,
  PromptInputTextarea,
  usePromptInputAttachments,
  type PromptInputMessage,
} from "@/components/ai-elements/prompt-input";
import {
  IconArrowRight,
  IconImageUpload,
  IconMicrophone,
} from "./ConciergeIcons";
import React, { useCallback, useRef, useState, useEffect } from "react";
import { toast } from "sonner";

const MAX_FILE_SIZE = 10 * 1024 * 1024; // 10MB
const ACCEPTED_IMAGE_TYPES = [
  "image/jpeg",
  "image/png",
  "image/webp",
  "image/gif",
];

interface PromptInputConciergeProps {
  placeholder?: string;
  onSubmit: (text: string, imageData?: string, imageMimeType?: string) => void;
  disabled?: boolean;
  value?: string;
  onChange?: (value: string) => void;
  onAttachClick?: () => void;
  onMicClick?: () => void;
  isStreaming?: boolean;
  /** Direction the input expands when content grows. Default is "up" */
  expandDirection?: "up" | "down";
  /** Maximum height for the textarea in pixels */
  maxHeight?: number;
}

export const PromptInputConcierge: React.FC<PromptInputConciergeProps> = ({
  placeholder = "Ask anything",
  onSubmit,
  disabled = false,
  isStreaming = false,
  value,
  onChange,
  expandDirection = "up",
  maxHeight,
}) => {
  const textareaRef = useRef<HTMLTextAreaElement>(null);
  const [isMultiline, setIsMultiline] = useState(false);
  const [hasAttachments, setHasAttachments] = useState(false);

  useEffect(() => {
    const textarea = textareaRef.current;
    if (!textarea) return;

    const checkMultiline = () => {
      const lineHeight = parseInt(getComputedStyle(textarea).lineHeight) || 21;
      const scrollHeight = textarea.scrollHeight;
      const paddingTop = parseInt(getComputedStyle(textarea).paddingTop) || 0;
      const paddingBottom =
        parseInt(getComputedStyle(textarea).paddingBottom) || 0;
      const contentHeight = scrollHeight - paddingTop - paddingBottom;
      setIsMultiline(contentHeight > lineHeight * 1.5);
    };

    checkMultiline();

    const observer = new ResizeObserver(checkMultiline);
    observer.observe(textarea);

    return () => observer.disconnect();
  }, [value]);

  const handleSubmit = useCallback(
    async (message: PromptInputMessage) => {
      if (
        !message.text?.trim() &&
        (!message.files || message.files.length === 0)
      ) {
        return;
      }

      // Handle image attachment
      let imageData: string | undefined;
      let imageMimeType: string | undefined;

      if (message.files && message.files.length > 0) {
        const file = message.files[0]; // Only handle first image for now

        // Validate file type
        if (!ACCEPTED_IMAGE_TYPES.includes(file.mediaType || "")) {
          toast.error(
            "Please upload a valid image file (JPEG, PNG, WebP, or GIF)"
          );
          return;
        }

        imageMimeType = file.mediaType;

        // Extract base64 data from data URL or blob URL
        if (file.url) {
          if (file.url.startsWith("data:")) {
            // Data URL - extract base64 by splitting (faster than regex for large strings)
            const dataUrlPrefix = "data:";
            const base64Prefix = ";base64,";

            try {
              if (file.url.includes(base64Prefix)) {
                const mimeTypeEnd = file.url.indexOf(base64Prefix);
                imageMimeType = file.url.substring(
                  dataUrlPrefix.length,
                  mimeTypeEnd
                );
                imageData = file.url.substring(
                  mimeTypeEnd + base64Prefix.length
                );
              } else {
                console.error("Data URL doesn't contain base64 marker");
              }
            } catch (error) {
              console.error("Failed to extract base64 from data URL:", error);
            }
          } else if (file.url.startsWith("blob:")) {
            // Blob URL - fetch and convert to base64
            try {
              const response = await fetch(file.url);
              const blob = await response.blob();
              const reader = new FileReader();

              imageData = await new Promise<string>((resolve, reject) => {
                reader.onloadend = () => {
                  const result = reader.result as string;
                  // Extract base64 data from data URL
                  const base64Match = result.match(/^data:[^;]+;base64,(.+)$/);
                  if (base64Match) {
                    resolve(base64Match[1]);
                  } else {
                    reject(new Error("Failed to extract base64 data"));
                  }
                };
                reader.onerror = reject;
                reader.readAsDataURL(blob);
              });
            } catch (error) {
              console.error("Failed to read blob URL:", error);
              toast.error("Failed to read image file");
              return;
            }
          }
        }
      }

      // Submit with image data if present
      onSubmit(message.text || "", imageData, imageMimeType);
    },
    [onSubmit]
  );

  const handleError = useCallback((err: { code: string; message: string }) => {
    if (err.code === "max_file_size") {
      toast.error("File size exceeds 10MB limit");
    } else if (err.code === "accept") {
      toast.error("Please upload a valid image file");
    } else {
      toast.error(err.message);
    }
  }, []);

  // CSS to make InputGroup completely invisible - no visual styles
  const inputGroupClasses = [
    "!tw:border-none",
    "!tw:shadow-none",
    "!tw:bg-transparent",
    "!tw:p-0",
    "!tw:m-0",
    "!tw:min-h-0",
    "!tw:h-auto",
    "!tw:ring-0",
    "!tw:outline-none",
  ].join(" ");

  const inputGroupStyle = {
    border: "none",
    boxShadow: "none",
  };

  // Use 20px radius when multiline OR has attachments
  const borderRadius = isMultiline || hasAttachments ? "20px" : "100px";

  return (
    <div className="tw:mx-auto tw:w-full tw:max-w-[768px]">
      {/* Pill container - dynamic border radius based on content */}
      <div
        style={{
          borderRadius,
          border: "1px solid #DCDDDC",
          transition: "border-radius 0.15s ease-out",
        }}
      >
        <PromptInput
          onSubmit={handleSubmit}
          multiple={false}
          globalDrop
          accept="image/*"
          maxFiles={1}
          maxFileSize={MAX_FILE_SIZE}
          onError={handleError}
          className="tw:w-full"
          inputGroupClassName={inputGroupClasses}
          inputGroupStyle={inputGroupStyle}
        >
          <PromptInputBody className="tw:flex tw:flex-col tw:w-full">
            {/* Attachments shown above text input */}
            <AttachmentsRow onAttachmentsChange={setHasAttachments} />

            {/* Text input row with buttons */}
            <div className="tw:flex tw:items-end tw:w-full">
              {/* Text input - takes remaining space */}
              <div className="tw:flex-1 tw:min-w-0">
                <PromptInputTextarea
                  ref={textareaRef}
                  placeholder={placeholder}
                  disabled={disabled}
                  value={value}
                  onChange={(e) => onChange?.(e.target.value)}
                  rows={1}
                  style={{
                    minHeight: 0,
                    paddingTop: 12,
                    paddingBottom: 12,
                    ...(maxHeight ? { maxHeight, overflowY: "auto" } : {}),
                  }}
                  className="tw:self-center"
                />
              </div>

              {/* Icons on the right - fixed, doesn't shrink */}
              <div className="tw:flex tw:items-center tw:gap-4 tw:shrink-0 tw:pb-3 tw:pr-3">
                {/* Attachment button for image upload */}
                <AttachmentButton disabled={disabled || isStreaming} />
                {/* Microphone for speech-to-text dictation */}
                <MicrophoneButton
                  disabled={disabled || isStreaming}
                  textareaRef={textareaRef}
                  onChange={onChange}
                />
                {/* Submit button - black circle */}
                <SubmitButton disabled={disabled || isStreaming} />
              </div>
            </div>
          </PromptInputBody>
        </PromptInput>
      </div>
    </div>
  );
};

// Separate component to render attachments above text and track attachment state
const AttachmentsRow: React.FC<{
  onAttachmentsChange: (hasAttachments: boolean) => void;
}> = ({ onAttachmentsChange }) => {
  const attachments = usePromptInputAttachments();

  useEffect(() => {
    onAttachmentsChange(attachments.files.length > 0);
  }, [attachments.files.length, onAttachmentsChange]);

  if (attachments.files.length === 0) {
    return null;
  }

  return (
    <div className="tw:px-3 tw:pt-3">
      <PromptInputAttachments>
        {(attachment) => <PromptInputAttachment data={attachment} />}
      </PromptInputAttachments>
    </div>
  );
};

// Separate component to use the hook inside PromptInput context
const AttachmentButton: React.FC<{ disabled: boolean }> = ({ disabled }) => {
  const attachments = usePromptInputAttachments();

  return (
    <button
      type="button"
      disabled={disabled}
      onClick={() => attachments.openFileDialog()}
      aria-label="Attach image"
      className="tw:size-6 tw:p-0 tw:bg-transparent tw:border-none tw:cursor-pointer disabled:tw:opacity-50 disabled:tw:cursor-not-allowed"
    >
      <IconImageUpload size={24} className="tw:text-primary" />
    </button>
  );
};

// Microphone button for speech-to-text dictation
const MicrophoneButton: React.FC<{
  disabled: boolean;
  textareaRef: React.RefObject<HTMLTextAreaElement | null>;
  onChange?: (value: string) => void;
}> = ({ disabled, textareaRef, onChange }) => {
  return (
    <PromptInputSpeechButton
      disabled={disabled}
      textareaRef={textareaRef}
      onTranscriptionChange={onChange}
      aria-label="Voice input"
      className="tw:size-6 tw:p-0 tw:bg-transparent tw:shadow-none tw:border-none tw:cursor-pointer hover:tw:bg-transparent disabled:tw:opacity-50 disabled:tw:cursor-not-allowed"
    >
      {(isListening) => (
        <div className="tw:relative tw:cursor-pointer">
          <IconMicrophone
            size={24}
            className={`tw:size-6 ${
              isListening ? "tw:text-red-500" : "tw:text-primary"
            }`}
          />
          {isListening && (
            <span className="tw:absolute tw:-top-0.5 tw:-right-0.5 tw:size-2 tw:rounded-full tw:bg-red-500 tw:animate-pulse" />
          )}
        </div>
      )}
    </PromptInputSpeechButton>
  );
};

// Submit button - black circle with white arrow pointing right
const SubmitButton: React.FC<{ disabled: boolean }> = ({ disabled }) => {
  return (
    <button
      type="submit"
      disabled={disabled}
      aria-label="Submit"
      className="tw:flex tw:items-center tw:justify-center tw:size-6 tw:rounded-[14px] tw:bg-primary tw:text-white tw:cursor-pointer tw:transition-opacity disabled:tw:opacity-50 disabled:tw:cursor-not-allowed"
    >
      <IconArrowRight size={12} className="tw:text-white" />
    </button>
  );
};
